import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Loader2, CheckCircle, Users, Star, Clock } from "lucide-react";
import { toast } from "sonner";
import { PageSEO } from "@/components/SEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BetaApplyPage = () => {
  const [form, setForm] = useState({ name: "", email: "", reason: "", social_handle: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.reason) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/beta/apply`, form);
      if (res.data.success) {
        setSubmitted(true);
        toast.success("Application submitted!");
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main data-testid="beta-apply-page" className="pt-20">
        <PageSEO title="Beta Tester Application" description="Apply to be a beta tester for the Burnout To Balance In Action course." path="/beta" />
        <section className="py-24 md:py-32 min-h-[70vh] flex items-center justify-center">
          <div className="max-w-lg mx-auto px-6 text-center">
            <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-8" style={{ background: "var(--ll-green)" }}>
              <CheckCircle size={40} style={{ color: "white" }} />
            </div>
            <h1 className="font-serif text-3xl font-light mb-4" style={{ color: "var(--ll-text)" }}>Application Received!</h1>
            <p className="text-base leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              Thank you for your interest! We're reviewing applications and will email you with your access pass if selected.
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="beta-apply-page" className="pt-20">
      <PageSEO title="Beta Tester Application" description="Apply to be a beta tester for the Burnout To Balance In Action course. Get free access and help shape the future of wellness education." path="/beta" />
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Info */}
            <div>
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6" style={{ background: "var(--ll-accent)", color: "var(--ll-text)" }}>
                <Sparkles size={14} />
                <span className="text-xs font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>Limited Beta Access</span>
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl font-light leading-[1.1] mb-6" style={{ color: "var(--ll-text)" }}>
                Be One of Our First{" "}
                <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>Course Testers</em>
              </h1>
              <p className="text-base leading-relaxed mb-8" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                We're inviting a small group of people to experience <strong>Burnout - To Balance In Action</strong> before it launches publicly. In exchange for your honest review, you'll get full access to the course — completely free.
              </p>

              <div className="space-y-5 mb-8">
                {[
                  { icon: Star, title: "Full course access", desc: "All 10 video lessons, quizzes, and EQ exercises" },
                  { icon: Clock, title: "30-day access pass", desc: "Complete at your own pace within the access window" },
                  { icon: Users, title: "Shape the course", desc: "Your feedback directly improves the experience for others" },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "var(--ll-bg-secondary)" }}>
                      <item.icon size={18} style={{ color: "var(--ll-green)" }} />
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{item.title}</p>
                      <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                Only a limited number of spots available. We'll review every application personally.
              </p>
            </div>

            {/* Right: Form */}
            <form onSubmit={handleSubmit} data-testid="beta-apply-form"
              className="p-8 rounded-2xl space-y-5"
              style={{ background: "white", border: "1px solid var(--ll-border)" }}>
              <h2 className="font-serif text-2xl font-medium mb-1" style={{ color: "var(--ll-text)" }}>Apply for Free Access</h2>
              <p className="text-xs mb-4" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>All fields marked * are required.</p>

              <div className="space-y-2">
                <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Full Name *</Label>
                <Input data-testid="beta-name" name="name" value={form.name} onChange={handleChange} required placeholder="Your name" className="rounded-xl border bg-white/50" style={{ borderColor: "var(--ll-border)" }} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Email *</Label>
                <Input data-testid="beta-email" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@example.com" className="rounded-xl border bg-white/50" style={{ borderColor: "var(--ll-border)" }} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Why do you want to try this course? *</Label>
                <Textarea data-testid="beta-reason" name="reason" value={form.reason} onChange={handleChange} required placeholder="Tell us a bit about yourself and why this course interests you..." rows={4} className="rounded-xl border bg-white/50 resize-none" style={{ borderColor: "var(--ll-border)" }} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Social Media Handle (optional)</Label>
                <Input data-testid="beta-social" name="social_handle" value={form.social_handle} onChange={handleChange} placeholder="@yourhandle (Instagram, LinkedIn, Twitter)" className="rounded-xl border bg-white/50" style={{ borderColor: "var(--ll-border)" }} />
                <p className="text-[11px]" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>We'll tag you if you share your review on social media!</p>
              </div>

              <button type="submit" data-testid="beta-submit" disabled={loading}
                className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BetaApplyPage;
